import { useReduxState } from 'hooks';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getCustomizedNavTitle } from 'lib/helpers';
import { breakpoints } from 'styles';
import { shouldShowAccountDeleteRequests } from 'lib/users/';
import { Link } from 'components/common';

import type { NavSection } from 'lib/helpers/getCustomizedNavTitle';
import type { LinkInterface } from 'components/common/Link';

type ExtendedLink = LinkInterface & {
  activePathname?: string;
  alignRight?: boolean;
  disabled?: boolean;
  title: string;
  tooltip?: string;
  unreadItems?: number;
};

const SubHeader = ({ activeLink }: { activeLink: string }) => {
  const {
    accountDeleteRequests,
    language,
    messages,
    serverConfig,
    supervisorUnreviewedItems,
    theme,
    user
  } = useReduxState();

  const getNavTitle = (section: NavSection, defaultTitle: string) => {
    return getCustomizedNavTitle({
      defaultTitle,
      language,
      navLinksCustomTexts: serverConfig.navLinksCustomTexts,
      section
    });
  };

  const userIsAdmin = user?.roles.includes('admin');
  const userIsSuperadmin = user?.roles.includes('superadmin');

  const userCanSeeOtherProfiles =
    user?.roles.includes('user') &&
    serverConfig.userEnableOtherProfiles.isEnabled &&
    (!serverConfig.userEnableOtherProfiles.conditions?.length ||
      attributesConditionsMatch({
        conditions: serverConfig.userEnableOtherProfiles.conditions,
        customAttributeValues: user.customAttributes,
        user
      }));

  const showAccountDeleteRequests = shouldShowAccountDeleteRequests({
    accountDeleteRequests,
    serverConfig,
    user
  });

  const links: ExtendedLink[] = [];

  if (
    (user?.roles.includes('guest') &&
      serverConfig.accountGuestEnableHomePage) ||
    user?.roles.includes('user')
  ) {
    links.push({
      href: '/',
      title: getNavTitle('home', messages.headerHome),
      disabled: serverConfig.platformCompetenciesOnly
    });
  }

  if (user?.roles.includes('user')) {
    links.push(
      {
        href: '/courses',
        title: getNavTitle('courses', messages.headerCourses),
        disabled:
          serverConfig.hideCoursesFromUI ||
          serverConfig.navLinksDisabled.courses
      },
      {
        href: '/paths',
        title: getNavTitle('paths', messages.paths),
        disabled:
          serverConfig.hidePathsFromUI || serverConfig.navLinksDisabled.paths
      },
      {
        href: '/achievements',
        title: getNavTitle('achievements', messages.commonAchievementsAlt),
        disabled:
          serverConfig.hideAchievementsFromUI ||
          serverConfig.navLinksDisabled.achievements
      },
      {
        href: '/calendar',
        title: getNavTitle('calendar', messages.eventCalendar),
        disabled:
          serverConfig.hideCalendarEventsFromUI ||
          serverConfig.navLinksDisabled.calendar
      },
      ...(serverConfig.featureFlags.enableCompetencyImprovements &&
      (userIsSuperadmin ||
        (userIsAdmin &&
          (serverConfig.competenciesAllowAdmins ||
            serverConfig.competencyProfilesAllowAdmins ||
            serverConfig.competenciesAllowMatrixForAdmins)))
        ? [
            {
              activePathname: '/competencies',
              href:
                userIsSuperadmin || serverConfig.competenciesAllowAdmins
                  ? '/competencies'
                  : serverConfig.competencyProfilesAllowAdmins
                  ? '/competencies/profiles'
                  : '/competencies/matrix',
              title: getNavTitle(
                'competencies',
                messages.analyticsCompetencies
              ),
              disabled: serverConfig.navLinksDisabled.competencies
            }
          ]
        : []),
      ...(userIsAdmin || userCanSeeOtherProfiles
        ? [
            {
              href: '/users',
              title: getNavTitle('users', messages.commonUsers),
              unreadItems: showAccountDeleteRequests
                ? accountDeleteRequests
                : null,
              tooltip:
                showAccountDeleteRequests && accountDeleteRequests
                  ? `${accountDeleteRequests} ${messages.usersAccountDeleteRequests?.toLowerCase()}`
                  : null,
              disabled: serverConfig.navLinksDisabled.users
            }
          ]
        : []),
      ...(userIsAdmin || user?.roles.includes('group-admin')
        ? [
            {
              href: '/groups',
              title: getNavTitle('groups', messages.commonGroups),
              disabled: serverConfig.navLinksDisabled.groups
            }
          ]
        : []),
      ...(user?.roles?.includes('supervisor')
        ? [
            {
              href: '/my-team',
              title: getNavTitle('myteam', messages.myTeamPage),
              unreadItems: supervisorUnreviewedItems,
              tooltip: supervisorUnreviewedItems
                ? getMessageWithPlaceholders({
                    message: messages.myTeamUnreviewedTooltip,
                    placeholders: { numberOfItems: supervisorUnreviewedItems }
                  })
                : null,
              disabled: serverConfig.navLinksDisabled.myteam
            }
          ]
        : []),
      {
        href: '/profile',
        title: getNavTitle('profile', messages.headerProfile),
        disabled: serverConfig.navLinksDisabled.profile
      },
      ...(user?.roles.includes('admin')
        ? [
            {
              href: '/analytics',
              title: getNavTitle('analytics', messages.commonAnalytics),
              disabled: serverConfig.navLinksDisabled.analytics
            }
          ]
        : [])
    );
  }

  if (userIsSuperadmin) {
    links.push({
      href: '/admin/admins',
      title: messages.headerSuperadmin,
      disabled: false
    });
  } else if (user?.permissions.includes('manage-email-templates')) {
    links.push({
      href: '/admin/settings/email',
      title: messages.adminSettings,
      disabled: false
    });
  } else if (
    user?.permissions.includes('edit-user-custom-attributes') &&
    serverConfig?.userCustomAttributes?.length
  ) {
    links.push({
      href: '/admin/settings/user-attributes',
      title: messages.adminSettings,
      disabled: false
    });
  }

  // add custom page links
  if (serverConfig?.customPages?.length) {
    const enabledPages = serverConfig.customPages.filter(
      (customPage) => customPage.isEnabled
    );
    enabledPages.forEach((customPage) => {
      const navTitle =
        customPage.navTitleTranslations?.[language] || customPage.navTitle;
      if (!customPage.routePath || !navTitle) return;
      links.splice(customPage.navOrderIndex, 0, {
        href: customPage.routePath,
        title: navTitle,
        disabled: false
      });
    });
  }

  return (
    <div className="sub-header">
      <div className="container">
        <div className="links">
          {links
            .filter((link) => !link.disabled)
            .map((link, i) => (
              <Link href={link.href} key={i} linkColor="inherit">
                <div
                  className={
                    'link' +
                    ((link.activePathname ?? link.href) === activeLink
                      ? ' active'
                      : '') +
                    (link.alignRight ? ' link-right' : '')
                  }
                  {...(link.tooltip ? { 'data-tooltip': link.tooltip } : {})}
                >
                  {link.title}
                  {link.unreadItems ? (
                    <div className="unread-items">{link.unreadItems}</div>
                  ) : null}
                </div>
              </Link>
            ))}
        </div>
      </div>

      <style jsx>{`
        .sub-header {
          background-color: ${theme.subHeaderBackgroundColor};
          overflow-y: auto;
          white-space: nowrap;
        }
        .link {
          border-bottom: 2px solid transparent;
          color: ${theme.subHeaderColor};
          display: inline-block;
          font-size: 1.4rem;
          font-weight: 700;
          outline: 0;
          margin-right: 25px;
          padding: 10px 2px 10px;
          position: relative;
          text-decoration: none;
        }
        .link.active,
        .link:hover,
        .link:focus {
          border-color: ${theme.subHeaderColorActive};
          color: ${theme.subHeaderColorActive};
        }
        .unread-items {
          background-color: ${theme.subHeaderColorActive};
          border-radius: 10px;
          color: ${theme.subHeaderBackgroundColor};
          font-size: 1rem;
          font-weight: 700;
          line-height: 1;
          padding: 2px 5px;
          position: absolute;
          right: -12px;
          top: 4px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .link-right {
            float: right;
            margin-right: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SubHeader;
